import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { supabase } from '@/lib/supabase';

interface Advertisement {
  id: string;
  title: string;
  description: string | null;
  image_url: string;
  link_url: string | null;
  display_seconds: number;
}

export function AdBanner() {
  const [currentAd, setCurrentAd] = useState<Advertisement | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [lastShownTime, setLastShownTime] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Check if we should show an ad based on frequency
    const checkAndShowAd = async () => {
      // Get last shown time from localStorage
      const storedLastShown = localStorage.getItem('lastAdShown');
      const lastShown = storedLastShown ? parseInt(storedLastShown, 10) : null;
      setLastShownTime(lastShown);

      // Determine if we should show an ad based on frequency
      const now = Date.now();
      const frequencyMap = {
        low: 24 * 60 * 60 * 1000, // 24 hours
        medium: 2 * 60 * 60 * 1000, // 2 hours
        high: 30 * 60 * 1000 // 30 minutes
      };

      // Get the frequency setting from the database
      const { data: settings, error: settingsError } = await supabase
        .from('advertisements')
        .select('display_frequency')
        .eq('is_active', true)
        .limit(1)
        .maybeSingle();

      if (settingsError) {
        console.error('Error fetching ad settings:', settingsError);
        return;
      }

      const frequency = settings?.display_frequency || 'medium';
      const minTimeBetweenAds = frequencyMap[frequency as keyof typeof frequencyMap];

      // If enough time has passed since the last ad, or no ad has been shown yet
      if (!lastShown || (now - lastShown > minTimeBetweenAds)) {
        fetchRandomAd();
      }
    };

    checkAndShowAd();
    
    // Set up interval to periodically check if we should show an ad
    const interval = setInterval(checkAndShowAd, 5 * 60 * 1000); // Check every 5 minutes

    return () => clearInterval(interval);
  }, []);

  const fetchRandomAd = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('advertisements')
        .select()
        .eq('is_active', true)
        .lte('start_date', new Date().toISOString())
        .gte('end_date', new Date().toISOString())
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error('Error fetching advertisement:', error);
        return;
      }

      if (data) {
        setCurrentAd(data);
        setIsVisible(true);
        
        // Store the current time as the last shown time
        const now = Date.now();
        localStorage.setItem('lastAdShown', now.toString());
        setLastShownTime(now);

        // Hide the ad after the specified display time
        setTimeout(() => {
          setIsVisible(false);
        }, data.display_seconds * 1000);
      }
    } catch (err) {
      console.error('Error in fetchRandomAd:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setIsVisible(false);
  };

  const handleAdClick = () => {
    if (currentAd?.link_url) {
      window.open(currentAd.link_url, '_blank');
    }
  };

  if (!isVisible || !currentAd) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50 animate-in fade-in duration-300 px-4 py-6">
      <div className="relative max-w-md w-full mx-auto bg-white rounded-lg shadow-xl overflow-hidden">
        <button
          onClick={handleClose}
          className="absolute top-2 right-2 p-1 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
        >
          <X className="h-5 w-5 text-gray-600" />
        </button>
        
        <div 
          className="cursor-pointer"
          onClick={handleAdClick}
        >
          <div className="relative w-full max-h-[60vh] overflow-hidden">
            <img 
              src={currentAd.image_url} 
              alt={currentAd.title} 
              className="w-full h-auto object-contain"
            />
          </div>
          
          <div className="p-4">
            <h3 className="text-lg font-bold text-gray-900">{currentAd.title}</h3>
            {currentAd.description && (
              <p className="mt-1 text-sm text-gray-600">{currentAd.description}</p>
            )}
          </div>
        </div>
        
        <div className="px-4 py-2 bg-gray-50 text-xs text-gray-500 text-right">
          Advertisement
        </div>
      </div>
    </div>
  );
}